import { useCallback, useEffect, useMemo, useState } from 'react';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { EventToolbox } from 'client/utils/event-toolbox';
import { transformVin } from 'site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { TrackingConstant } from 'client/tracking/constant';
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'client/site-modules/shared/constants/appraisal/appraisal';
import { APPRAISAL_STORAGE_KEYS } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/constants';

export function useVinFormState({
  vin,
  queryVin,
  profileOfferVin,
  isLookUpStyleView,
  isVinEdited,
  setIsVinEdited,
  wasVinPrePopulated,
  setWasVinPrePopulated,
  storage,
}) {
  const [vinInputValue, setVinInputValue] = useState(vin);
  const [isVinValid, setIsVinValid] = useState(validation.validateVIN(vin));
  const [isVinValidLength, setIsVinValidLength] = useState(validation.validateVinLength(vin));
  const [wasFormSubmitted, setWasFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const prepopulatedVin = useMemo(() => transformVin(queryVin || profileOfferVin || ''), [queryVin, profileOfferVin]);

  const applyVinValue = useCallback(
    ({ nextVin, shouldMarkEdited = true, shouldPersist = true }) => {
      const transformedVin = transformVin(nextVin);

      if (shouldMarkEdited) {
        setIsVinEdited(true);
      }

      if (storage && shouldPersist) {
        storage.set(APPRAISAL_STORAGE_KEYS.VIN, transformedVin);
      }

      setVinInputValue(transformedVin);
      setIsVinValid(validation.validateVIN(transformedVin));
      setIsVinValidLength(validation.validateVinLength(transformedVin));
      setWasFormSubmitted(false);
      setIsSubmitting(false);
    },
    [setIsVinEdited, storage]
  );

  useEffect(() => {
    if (!prepopulatedVin || vinInputValue || isVinEdited) {
      return;
    }

    if (prepopulatedVin === profileOfferVin && !isLookUpStyleView && !wasVinPrePopulated) {
      setWasVinPrePopulated(true);
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          subaction_name: TrackingConstant.VIN_AUTOFILL,
          creative_id: APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID,
        },
      });
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- initialize form state from URL/profile input after mount.
    applyVinValue({
      nextVin: prepopulatedVin,
      shouldMarkEdited: false,
      shouldPersist: false,
    });
  }, [
    applyVinValue,
    isLookUpStyleView,
    isVinEdited,
    prepopulatedVin,
    profileOfferVin,
    setWasVinPrePopulated,
    vinInputValue,
    wasVinPrePopulated,
  ]);

  useEffect(() => {
    if (!storage || vinInputValue) {
      return;
    }

    const storedVin = storage.get(APPRAISAL_STORAGE_KEYS.VIN);
    let storageVin;
    try {
      storageVin = JSON.parse(storedVin);
    } catch {
      storageVin = storedVin;
    }

    if (storageVin) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initialize form state from persisted input after mount.
      applyVinValue({ nextVin: storageVin });
    }
  }, [applyVinValue, storage, vinInputValue]);

  const onVinChange = useCallback(
    e => {
      applyVinValue({ nextVin: e.target.value?.toString() });
    },
    [applyVinValue]
  );

  return {
    vinInputValue,
    isVinValid,
    isVinValidLength,
    wasFormSubmitted,
    isSubmitting,
    setWasFormSubmitted,
    setIsSubmitting,
    onVinChange,
  };
}
